"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { trackGoogleAdsEvent } from "@/lib/google-ads-events";
import { initJourneyTracker, trackPageView } from "@/lib/journey-tracker";

/**
 * Journey Tracker Initialization Component
 *
 * Initializes journey tracking on app load and tracks page views
 * on route changes. Should be placed in the root layout.
 */
export function JourneyTrackerInit() {
	const pathname = usePathname();
	const isInitialized = useRef(false);
	const lastPath = useRef<string | null>(null);

	// Initialize tracker on first mount
	useEffect(() => {
		if (!isInitialized.current) {
			initJourneyTracker();
			isInitialized.current = true;
			lastPath.current = pathname;
		}
	}, [pathname]);

	// Track page views on route changes
	useEffect(() => {
		if (isInitialized.current && pathname !== lastPath.current) {
			trackPageView();
			lastPath.current = pathname;
		}
	}, [pathname]);

	useEffect(() => {
		const trackAnnotatedClick = (event: MouseEvent) => {
			if (!(event.target instanceof Element)) return;
			const target = event.target.closest<HTMLElement>(
				"[data-analytics-event]",
			);
			const eventName = target?.dataset.analyticsEvent;
			if (!eventName) return;

			trackGoogleAdsEvent(eventName, {
				event_category: "sales_funnel",
				event_label: target.dataset.analyticsLabel,
				page_path: pathname,
			});
		};

		document.addEventListener("click", trackAnnotatedClick);
		return () => document.removeEventListener("click", trackAnnotatedClick);
	}, [pathname]);

	return null;
}
